import { useEffect, useState } from "react";
import { confirm } from "../components/ConfirmDialog";
import { apiClient } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Package, Plus, Trash2, Pencil, Copy, Check, X } from "lucide-react";
import { toast } from "sonner";

const emptyForm = {
  plan_id: "",
  name: "",
  tagline: "",
  monthly_price_cents: 0,
  annual_price_cents: 0,
  features: [],
  limits: { max_employees: 0, max_pay_groups: 0, max_workspaces: 1 },
  stripe_monthly_price_id: "",
  stripe_annual_price_id: "",
  is_active: true,
  is_visible_on_landing: true,
  is_free: false,
  order: 100,
};

const dollars = (c) => `$${((c || 0) / 100).toFixed(2)}`;

export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchPlans = async () => {
    try {
      const res = await apiClient.get("/admin/plans");
      setPlans(res.data.plans || []);
    } catch {
      toast.error("Failed to load plans");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchPlans(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, order: (plans.length + 1) * 10 });
    setDialogOpen(true);
  };
  const openEdit = (plan) => {
    setEditing(plan);
    setForm({ ...emptyForm, ...plan, limits: plan.limits || emptyForm.limits });
    setDialogOpen(true);
  };
  const openDuplicate = (plan) => {
    setEditing(null);
    setForm({
      ...emptyForm,
      ...plan,
      plan_id: "",
      name: `${plan.name} (Copy)`,
      order: plan.order + 1,
    });
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Strip UI-only fields (prefixed with _) before sending to backend
      const cleanForm = Object.fromEntries(
        Object.entries(form).filter(([k]) => !k.startsWith("_"))
      );
      if (editing) {
        const { plan_id: _skip, ...patch } = cleanForm;
        await apiClient.patch(`/admin/plans/${editing.plan_id}`, patch);
        toast.success("Plan updated");
      } else {
        await apiClient.post("/admin/plans", cleanForm);
        toast.success("Plan created");
      }
      setDialogOpen(false);
      fetchPlans();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (plan) => {
    const ok = await confirm({
      title: `Delete plan "${plan.name}"?`,
      description: "If any tenants are currently on this plan the delete will be blocked. Consider disabling the plan (uncheck \"Active\") instead so existing subscribers keep their features but no new tenants can select it.",
      confirmText: "Delete plan",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await apiClient.delete(`/admin/plans/${plan.plan_id}`);
      toast.success("Deleted");
      fetchPlans();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  const toggleFeature = (idx) => {
    const next = [...(form.features || [])];
    next[idx] = { ...next[idx], included: !next[idx].included };
    setForm({ ...form, features: next });
  };
  const addFeature = () => setForm({ ...form, features: [...(form.features || []), { key: "", label: "", included: true }] });
  const removeFeature = (idx) => setForm({ ...form, features: form.features.filter((_, i) => i !== idx) });

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl space-y-6" data-testid="admin-plans-page">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-[#0A192F] font-['Manrope'] tracking-tight flex items-center gap-2">
              <Package className="w-7 h-7 text-[#0D9488]" /> Plans & Pricing
            </h1>
            <p className="text-slate-600 mt-1">
              Control the tiers, prices, features and limits shown to customers on the landing page and Settings → Plan tab.
            </p>
          </div>
          <Button onClick={openCreate} className="bg-[#0D9488] hover:bg-[#14B8A6]" data-testid="admin-plans-add-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Plan
          </Button>
        </div>

        <Card>
          <CardHeader><CardTitle className="text-lg">All Plans</CardTitle></CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-32 bg-slate-100 rounded animate-pulse" />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Monthly</TableHead>
                    <TableHead>Annual</TableHead>
                    <TableHead>Features</TableHead>
                    <TableHead>Visibility</TableHead>
                    <TableHead className="w-16 text-right">Order</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-testid="admin-plans-table-body">
                  {plans.map((p) => {
                    const included = (p.features || []).filter((f) => f.included).length;
                    const total = (p.features || []).length;
                    return (
                      <TableRow key={p.plan_id} data-testid={`admin-plan-row-${p.plan_id}`}>
                        <TableCell>
                          <div className="font-semibold text-[#0A192F]">{p.name}</div>
                          <div className="text-xs text-slate-500 line-clamp-1">{p.tagline}</div>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{p.is_free ? "Free" : dollars(p.monthly_price_cents)}</TableCell>
                        <TableCell className="font-mono text-sm">{p.is_free ? "Free" : dollars(p.annual_price_cents)}</TableCell>
                        <TableCell className="text-sm">{included}/{total} enabled</TableCell>
                        <TableCell>
                          <div className="flex gap-2 items-center text-xs">
                            <span className={`px-1.5 py-0.5 rounded ${p.is_active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                              {p.is_active ? "Active" : "Archived"}
                            </span>
                            {p.is_visible_on_landing && p.is_active && (
                              <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">Public</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right text-sm text-slate-500">{p.order}</TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="ghost" onClick={() => openEdit(p)}
                              data-testid={`admin-plan-edit-${p.plan_id}`}
                              className="text-slate-500 hover:text-[#0D9488]"><Pencil className="w-4 h-4" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => openDuplicate(p)}
                              data-testid={`admin-plan-duplicate-${p.plan_id}`}
                              className="text-slate-500 hover:text-blue-600"><Copy className="w-4 h-4" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => remove(p)}
                              data-testid={`admin-plan-delete-${p.plan_id}`}
                              className="text-rose-500 hover:text-rose-700"><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="admin-plan-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Plan" : "New Plan"}</DialogTitle>
            <DialogDescription>All prices are in USD dollars (e.g. 29 or 29.99). Set both prices to 0 for a free plan.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Plan ID (slug)</Label>
                <Input value={form.plan_id} disabled={!!editing}
                  onChange={(e) => setForm({ ...form, plan_id: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") })}
                  placeholder="starter" data-testid="admin-plan-id-input" required />
              </div>
              <div>
                <Label>Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Starter" data-testid="admin-plan-name-input" required />
              </div>
            </div>
            <div>
              <Label>Tagline</Label>
              <Textarea rows={2} maxLength={200} value={form.tagline}
                onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                data-testid="admin-plan-tagline-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Monthly price (USD)</Label>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={form._monthly_dollars ?? ((form.monthly_price_cents || 0) / 100).toString()}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const cents = Math.round((parseFloat(raw) || 0) * 100);
                    setForm({ ...form, _monthly_dollars: raw, monthly_price_cents: cents });
                  }}
                  data-testid="admin-plan-monthly-cents-input"
                  placeholder="29.00"
                />
                <p className="text-xs text-slate-500 mt-1">{dollars(form.monthly_price_cents)}/mo</p>
              </div>
              <div>
                <Label>Annual price (USD)</Label>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={form._annual_dollars ?? ((form.annual_price_cents || 0) / 100).toString()}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const cents = Math.round((parseFloat(raw) || 0) * 100);
                    setForm({ ...form, _annual_dollars: raw, annual_price_cents: cents });
                  }}
                  data-testid="admin-plan-annual-cents-input"
                  placeholder="290.00"
                />
                <p className="text-xs text-slate-500 mt-1">{dollars(form.annual_price_cents)}/yr</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Max employees</Label>
                <Input type="number" min={0} value={form.limits.max_employees}
                  onChange={(e) => setForm({ ...form, limits: { ...form.limits, max_employees: Number(e.target.value) || 0 } })} />
              </div>
              <div>
                <Label>Max pay groups</Label>
                <Input type="number" min={0} value={form.limits.max_pay_groups}
                  onChange={(e) => setForm({ ...form, limits: { ...form.limits, max_pay_groups: Number(e.target.value) || 0 } })} />
              </div>
              <div>
                <Label>Max workspaces</Label>
                <Input type="number" min={1} value={form.limits.max_workspaces}
                  onChange={(e) => setForm({ ...form, limits: { ...form.limits, max_workspaces: Number(e.target.value) || 1 } })} />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Features</Label>
                <Button type="button" size="sm" variant="outline" onClick={addFeature}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add feature
                </Button>
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {(form.features || []).map((f, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input placeholder="key" value={f.key} className="w-32"
                      onChange={(e) => { const n = [...form.features]; n[idx].key = e.target.value; setForm({ ...form, features: n }); }} />
                    <Input placeholder="Label shown to customer" value={f.label} className="flex-1"
                      onChange={(e) => { const n = [...form.features]; n[idx].label = e.target.value; setForm({ ...form, features: n }); }} />
                    <Button type="button" size="sm" variant="ghost" onClick={() => toggleFeature(idx)}
                      className={f.included ? "text-emerald-600" : "text-slate-400"}
                      aria-label="Toggle included">
                      {f.included ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                    </Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => removeFeature(idx)}
                      className="text-rose-500" aria-label="Remove"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Stripe monthly Price ID</Label>
                <Input value={form.stripe_monthly_price_id}
                  onChange={(e) => setForm({ ...form, stripe_monthly_price_id: e.target.value })}
                  placeholder="price_..." />
              </div>
              <div>
                <Label>Stripe annual Price ID</Label>
                <Input value={form.stripe_annual_price_id}
                  onChange={(e) => setForm({ ...form, stripe_annual_price_id: e.target.value })}
                  placeholder="price_..." />
              </div>
            </div>
            <div className="flex flex-wrap gap-6 pt-2">
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} /> Active
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.is_visible_on_landing} onCheckedChange={(v) => setForm({ ...form, is_visible_on_landing: v })} /> Show on landing
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.is_free} onCheckedChange={(v) => setForm({ ...form, is_free: v })} /> Free plan
              </label>
              <div className="flex items-center gap-2 text-sm">
                <Label>Order</Label>
                <Input type="number" className="w-20" value={form.order}
                  onChange={(e) => setForm({ ...form, order: Number(e.target.value) || 0 })} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="admin-plan-save-btn"
                className="bg-[#0D9488] hover:bg-[#14B8A6]">
                {saving ? "Saving..." : editing ? "Save Changes" : "Create Plan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
